export interface CityData {
  name: string;
  lat: number;
  lng: number;
}

export interface LocationData {
  slug: string;
  name: string;
  state: string;
  region: string;
  description: string;
  highlights: string[];
  content: string[];
  isPrimary: boolean;
  /** Optional background image for the featured service-area cards. */
  image?: string;
  lat: number;
  lng: number;
  cities: CityData[];
}

const PHONE = "(720) 586-3832";

export const locations: LocationData[] = [
  {
    slug: "highlands-ranch",
    image: "/assets/images/areas/area-highlands-ranch.webp",
    name: "Highlands Ranch",
    state: "CO",
    region: "Douglas County",
    isPrimary: true,
    lat: 39.5539,
    lng: -104.9689,
    description:
      "Diaz Brothers Construction LLC is headquartered in Highlands Ranch, CO. Interior and exterior painting, facade waterproofing, remodeling, drywall, and tile installation for homes and businesses across Douglas County.",
    highlights: [
      "Headquartered in Highlands Ranch, CO",
      "Painting, drywall, tile & remodeling",
      "Bilingual crew — English & Español",
      "Free written estimates",
    ],
    content: [
      "Highlands Ranch is home base. Our shop sits on Et. Country, and most of our crew lives within a few miles of it — which means short drive times, flexible scheduling, and a team that already knows the neighborhoods, the HOA color palettes, and the way these homes were built.",
      "The housing stock here is largely 1990s and 2000s construction, and that shows up in predictable ways: south- and west-facing siding that chalks from the sun, stucco with hairline cracking, popcorn ceilings owners want gone, and original builder-grade paint that is well past its service life. Those are the jobs we run every week.",
      `Call ${PHONE} for a free, no-pressure estimate anywhere in Highlands Ranch.`,
    ],
    cities: [
      { name: "Southridge", lat: 39.539, lng: -104.958 },
      { name: "Backcountry", lat: 39.523, lng: -104.999 },
      { name: "Northridge", lat: 39.566, lng: -104.976 },
      { name: "Lone Tree", lat: 39.5514, lng: -104.8861 },
    ],
  },
  {
    slug: "littleton",
    image: "/assets/images/areas/area-littleton.webp",
    name: "Littleton",
    state: "CO",
    region: "Arapahoe County",
    isPrimary: false,
    lat: 39.6133,
    lng: -105.0166,
    description:
      "Painting, drywall, tile, and remodeling contractors serving Littleton, CO. Residential and commercial interiors and exteriors, with free written estimates.",
    highlights: [
      "Interior & exterior painting",
      "Drywall repair & texture matching",
      "Kitchen & bathroom remodels",
      "Free written estimates",
    ],
    content: [
      "Littleton mixes historic downtown properties with mid-century ranches and newer builds out toward Ken Caryl — and each one needs a different approach. Older homes here often hide plaster behind the drywall and lead-era paint under the top coat, so prep matters more than it does on new construction.",
      "We handle full-home repaints, single-room refreshes, drywall and ceiling repair, tile floors and showers, and complete kitchen or basement remodels throughout Littleton. Commercial clients along Santa Fe and in the downtown district use us for storefront interiors and building facades.",
      `Call ${PHONE} to schedule a free Littleton estimate.`,
    ],
    cities: [
      { name: "Downtown Littleton", lat: 39.613, lng: -105.017 },
      { name: "Columbine", lat: 39.588, lng: -105.069 },
      { name: "Ken Caryl", lat: 39.5786, lng: -105.1128 },
      { name: "Bow Mar", lat: 39.635, lng: -105.045 },
    ],
  },
  {
    slug: "centennial",
    name: "Centennial",
    state: "CO",
    region: "Arapahoe County",
    isPrimary: false,
    lat: 39.5807,
    lng: -104.8772,
    description:
      "Centennial, CO painting and remodeling contractor. Interior and exterior painting, drywall, tile installation, and facade waterproofing for homes and commercial buildings.",
    highlights: [
      "Residential & commercial painting",
      "Basement finishing & remodels",
      "Popcorn ceiling removal",
      "15% new-client discount",
    ],
    content: [
      "Centennial homeowners call us most often for two things: exterior repaints that have to satisfy an HOA, and basement finishes that turn unused square footage into real living space. We do both, and we do the painting and drywall on the same job so there is no handoff between trades.",
      "Our crew works everywhere from the Southglenn area to the Arapahoe Road corridor, on single-family homes, townhomes, and small commercial buildings. Every estimate is written, itemized, and free.",
      `Reach us at ${PHONE} for pricing in Centennial.`,
    ],
    cities: [
      { name: "Southglenn", lat: 39.593, lng: -104.951 },
      { name: "Foxfield", lat: 39.594, lng: -104.75 },
      { name: "Piney Creek", lat: 39.596, lng: -104.777 },
    ],
  },
  {
    slug: "lone-tree",
    name: "Lone Tree",
    state: "CO",
    region: "Douglas County",
    isPrimary: false,
    lat: 39.5514,
    lng: -104.8861,
    description:
      "Lone Tree, CO interior and exterior painting, remodeling, drywall, and tile installation. Detail-focused work for homes, HOAs, and commercial spaces.",
    highlights: [
      "HOA-compliant exterior repaints",
      "Cabinet & trim refinishing",
      "Tile floors, showers & backsplashes",
      "Bilingual crew — English & Español",
    ],
    content: [
      "Lone Tree properties are held to a high standard, and most exterior work here runs through an HOA approval process. We are comfortable with that — we help match approved palettes, document the scope, and deliver a finish that holds up to the review.",
      "Inside, we do a lot of cabinet refinishing, trim and door repaints, and tile work in kitchens and primary baths. Commercial clients near Park Meadows use us for office interiors and building maintenance painting.",
      `Call ${PHONE} for a free Lone Tree estimate.`,
    ],
    cities: [
      { name: "RidgeGate", lat: 39.535, lng: -104.867 },
      { name: "Park Meadows", lat: 39.566, lng: -104.87 },
      { name: "Heritage Hills", lat: 39.559, lng: -104.883 },
    ],
  },
  {
    slug: "castle-pines",
    image: "/assets/images/areas/area-castle-pines.webp",
    name: "Castle Pines",
    state: "CO",
    region: "Douglas County",
    isPrimary: false,
    lat: 39.4581,
    lng: -104.8961,
    description:
      "Castle Pines, CO painting, waterproofing, and remodeling contractor. Exterior repaints, stucco sealing, drywall, tile, and full renovations.",
    highlights: [
      "Exterior repaints & stucco sealing",
      "Facade waterproofing",
      "Kitchen & bath remodels",
      "Free written estimates",
    ],
    content: [
      "Castle Pines sits high and exposed, which means exteriors take a beating from UV, wind-driven rain, and hail. Stucco and cedar here need sealing and recoating on a shorter cycle than homes down in the metro, and we plan the work accordingly.",
      "We handle full exterior repaints, elastomeric waterproof coatings on stucco and masonry, interior painting, drywall repair, tile installation, and complete kitchen or bathroom remodels throughout Castle Pines and Castle Pines Village.",
      `Call ${PHONE} to book a free Castle Pines estimate.`,
    ],
    cities: [
      { name: "Castle Pines Village", lat: 39.446, lng: -104.909 },
      { name: "The Canyons", lat: 39.428, lng: -104.86 },
      { name: "Daniels Gate", lat: 39.454, lng: -104.882 },
    ],
  },
  {
    slug: "parker",
    image: "/assets/images/areas/area-parker.webp",
    name: "Parker",
    state: "CO",
    region: "Douglas County",
    isPrimary: false,
    lat: 39.5186,
    lng: -104.7614,
    description:
      "Parker, CO painting and remodeling services. Interior and exterior painting, drywall, tile, basement finishing, and facade waterproofing.",
    highlights: [
      "Interior & exterior painting",
      "Basement finishing",
      "Drywall & popcorn ceiling removal",
      "10% referral discount",
    ],
    content: [
      "Parker has grown fast, and a lot of the homes built during that boom are hitting the age where builder-grade paint, caulk, and trim all need attention at once. That is exactly the kind of full-exterior package we price as a single project.",
      "We also finish basements, remove popcorn ceilings, repair and texture drywall, and install tile in kitchens and bathrooms across Parker, Stonegate, and the Mainstreet area.",
      `Call ${PHONE} for free pricing in Parker.`,
    ],
    cities: [
      { name: "Stonegate", lat: 39.545, lng: -104.783 },
      { name: "Mainstreet", lat: 39.518, lng: -104.761 },
      { name: "Canterberry Crossing", lat: 39.505, lng: -104.729 },
    ],
  },
  {
    slug: "ken-caryl",
    name: "Ken Caryl",
    state: "CO",
    region: "Jefferson County",
    isPrimary: false,
    lat: 39.5786,
    lng: -105.1128,
    description:
      "Ken Caryl, CO painting, drywall, and remodeling contractor. Exterior repaints built for foothill sun and wind, plus interior renovations.",
    highlights: [
      "Foothill-rated exterior coatings",
      "Cedar & siding repair before paint",
      "Interior painting & drywall",
      "Free written estimates",
    ],
    content: [
      "Ken Caryl homes sit right against the hogback, and that exposure is hard on wood siding and trim. We replace rotted boards, re-caulk every seam, and use coatings rated for high-UV foothill conditions rather than whatever is cheapest on the shelf.",
      "Interior work here runs the full range: whole-house repaints, drywall and ceiling repair, tile floors and showers, and kitchen or bath remodels in both Ken Caryl Ranch and the Valley.",
      `Call ${PHONE} for a free Ken Caryl estimate.`,
    ],
    cities: [
      { name: "Ken Caryl Ranch", lat: 39.564, lng: -105.14 },
      { name: "The Valley", lat: 39.575, lng: -105.153 },
      { name: "Deer Creek", lat: 39.548, lng: -105.13 },
    ],
  },
  {
    slug: "morrison",
    name: "Morrison",
    state: "CO",
    region: "Jefferson County",
    isPrimary: false,
    lat: 39.6533,
    lng: -105.19,
    description:
      "Morrison, CO painting, waterproofing, and remodeling. Exterior staining, stucco sealing, drywall, tile, and complete home renovations.",
    highlights: [
      "Exterior painting & stain",
      "Masonry & stucco waterproofing",
      "Drywall and tile work",
      "Bilingual crew — English & Español",
    ],
    content: [
      "Morrison properties range from historic stone and brick downtown to mountain homes up the canyon. Both need moisture management first and paint second — sealing masonry and re-flashing problem areas before any coating goes on.",
      "We handle exterior painting and staining, waterproof coatings on stone and stucco, interior repaints, drywall repair, and tile installation throughout Morrison and the surrounding canyons.",
      `Call ${PHONE} for a free Morrison estimate.`,
    ],
    cities: [
      { name: "Downtown Morrison", lat: 39.653, lng: -105.19 },
      { name: "Willowbrook", lat: 39.664, lng: -105.208 },
      { name: "Red Rocks", lat: 39.665, lng: -105.205 },
    ],
  },
  {
    slug: "evergreen",
    image: "/assets/images/areas/area-evergreen.webp",
    name: "Evergreen",
    state: "CO",
    region: "Jefferson County",
    isPrimary: false,
    lat: 39.6333,
    lng: -105.3172,
    description:
      "Evergreen, CO painting and remodeling contractor. Exterior stain and paint for mountain homes, plus drywall, tile, and full renovations.",
    highlights: [
      "Mountain home exterior stain & paint",
      "Log, cedar & timber refinishing",
      "Interior painting & drywall",
      "Free written estimates",
    ],
    content: [
      "Mountain homes in Evergreen live under punishing conditions — intense UV at 7,000 feet, heavy snow load, and dramatic freeze-thaw swings. Log, cedar, and timber exteriors here need cleaning, sanding, and a stain system built for altitude, not a single coat of whatever was on sale.",
      "We stain and paint mountain exteriors, refinish decks and railings, repair and texture drywall, install tile, and take on complete interior remodels throughout Evergreen and Bergen Park.",
      `Call ${PHONE} for a free Evergreen estimate.`,
    ],
    cities: [
      { name: "Bergen Park", lat: 39.687, lng: -105.35 },
      { name: "Hiwan", lat: 39.664, lng: -105.33 },
      { name: "Upper Bear Creek", lat: 39.62, lng: -105.36 },
    ],
  },
  {
    slug: "genesee",
    name: "Genesee",
    state: "CO",
    region: "Jefferson County",
    isPrimary: false,
    lat: 39.6939,
    lng: -105.2708,
    description:
      "Genesee, CO exterior staining, painting, and remodeling. High-altitude coatings, drywall, tile, and interior renovation for mountain properties.",
    highlights: [
      "High-altitude exterior coatings",
      "Deck & railing refinishing",
      "Interior remodels & drywall",
      "10% referral discount",
    ],
    content: [
      "Genesee homes sit above 7,500 feet with almost no shade, and that combination of UV and wind strips finishes faster than anywhere else we work. We use stain and coating systems specified for that exposure and prep aggressively before anything is applied.",
      "Along with exteriors, we handle interior painting, drywall and ceiling repair, tile installation, and full kitchen or bath remodels for Genesee properties.",
      `Call ${PHONE} for a free Genesee estimate.`,
    ],
    cities: [
      { name: "Genesee Village", lat: 39.702, lng: -105.269 },
      { name: "Lookout Mountain", lat: 39.735, lng: -105.239 },
      { name: "Chief Hosa", lat: 39.687, lng: -105.29 },
    ],
  },
  {
    slug: "conifer",
    image: "/assets/images/areas/area-conifer.webp",
    name: "Conifer",
    state: "CO",
    region: "Jefferson County",
    isPrimary: false,
    lat: 39.5222,
    lng: -105.3061,
    description:
      "Conifer, CO painting, staining, and remodeling contractor. Mountain exterior finishes, drywall, tile installation, and complete renovations.",
    highlights: [
      "Cedar & log home staining",
      "Exterior & interior painting",
      "Drywall, tile & remodeling",
      "Free written estimates",
    ],
    content: [
      "Conifer properties are spread out and often hard to schedule with contractors who only work the metro. We make the drive — and we plan mountain jobs around weather windows so a coat of stain does not get rained or snowed on before it cures.",
      "Services include exterior staining and painting, deck refinishing, interior repaints, drywall repair and texture matching, tile installation, and full remodels.",
      `Call ${PHONE} for a free Conifer estimate.`,
    ],
    cities: [
      { name: "Aspen Park", lat: 39.535, lng: -105.29 },
      { name: "Pine Junction", lat: 39.45, lng: -105.35 },
      { name: "Kings Valley", lat: 39.505, lng: -105.29 },
    ],
  },
  {
    slug: "sedalia",
    name: "Sedalia",
    state: "CO",
    region: "Douglas County",
    isPrimary: false,
    lat: 39.4386,
    lng: -104.9625,
    description:
      "Sedalia, CO painting, waterproofing, and remodeling services for homes, ranch properties, and outbuildings.",
    highlights: [
      "Home, barn & outbuilding painting",
      "Facade & masonry waterproofing",
      "Drywall, tile & remodeling",
      "Bilingual crew — English & Español",
    ],
    content: [
      "Sedalia is ranch and acreage country, and the work reflects that — main houses, guest houses, barns, shops, and fence lines that all need coating on a schedule. We price these as whole-property packages rather than piecemeal service calls.",
      "We also handle interior painting, drywall repair, tile installation, and kitchen or bathroom remodels for Sedalia homes.",
      `Call ${PHONE} for a free Sedalia estimate.`,
    ],
    cities: [
      { name: "Perry Park", lat: 39.263, lng: -104.98 },
      { name: "Louviers", lat: 39.472, lng: -105.0 },
      { name: "Roxborough", lat: 39.475, lng: -105.07 },
    ],
  },
  {
    slug: "franktown",
    name: "Franktown",
    state: "CO",
    region: "Douglas County",
    isPrimary: false,
    lat: 39.3897,
    lng: -104.7522,
    description:
      "Franktown, CO painting, drywall, tile, and remodeling contractor serving homes and acreage properties across eastern Douglas County.",
    highlights: [
      "Interior & exterior painting",
      "Drywall repair & texture matching",
      "Tile & remodeling",
      "Free written estimates",
    ],
    content: [
      "Franktown properties tend to sit on open acreage with full wind and sun exposure on every elevation. That means exteriors wear evenly and fast — and a repaint here is genuinely a whole-house job, not a couple of touched-up walls.",
      "We handle exterior and interior painting, drywall and ceiling repair, tile installation, and complete remodels throughout Franktown and the surrounding countryside.",
      `Call ${PHONE} for a free Franktown estimate.`,
    ],
    cities: [
      { name: "Russellville", lat: 39.345, lng: -104.72 },
      { name: "Castlewood", lat: 39.35, lng: -104.78 },
      { name: "Ponderosa Hills", lat: 39.41, lng: -104.79 },
    ],
  },
  {
    slug: "larkspur",
    name: "Larkspur",
    state: "CO",
    region: "Douglas County",
    isPrimary: false,
    lat: 39.1897,
    lng: -104.8894,
    description:
      "Larkspur, CO painting, staining, and remodeling. Exterior finishes for mountain and acreage homes, plus drywall, tile, and renovations.",
    highlights: [
      "Exterior stain & paint",
      "Deck & fence refinishing",
      "Drywall, tile & remodeling",
      "10% referral discount",
    ],
    content: [
      "Larkspur sits in the pines between Denver and Colorado Springs, and most homes here have significant exposed wood — siding, decks, railings, and fencing that all need periodic stain to survive the sun and snow.",
      "We stain and paint exteriors, refinish decks and fences, repaint interiors, repair drywall, install tile, and take on kitchen and bath remodels throughout Larkspur and Perry Park.",
      `Call ${PHONE} for a free Larkspur estimate.`,
    ],
    cities: [
      { name: "Perry Park", lat: 39.263, lng: -104.98 },
      { name: "Spruce Mountain", lat: 39.22, lng: -104.87 },
      { name: "Greenland", lat: 39.16, lng: -104.86 },
    ],
  },
  {
    slug: "elizabeth",
    name: "Elizabeth",
    state: "CO",
    region: "Elbert County",
    isPrimary: false,
    lat: 39.36,
    lng: -104.5989,
    description:
      "Elizabeth, CO painting, drywall, tile, and remodeling contractor serving Elbert County homes, shops, and outbuildings.",
    highlights: [
      "Interior & exterior painting",
      "Barn & outbuilding coatings",
      "Drywall, tile & remodeling",
      "Free written estimates",
    ],
    content: [
      "Elizabeth and the surrounding Elbert County properties are far enough out that a lot of contractors will not travel there. We will. We batch our work in this area so scheduling stays efficient and pricing stays fair.",
      "We handle full exterior repaints, barn and outbuilding coatings, interior painting, drywall repair, tile installation, and complete remodels.",
      `Call ${PHONE} for a free Elizabeth estimate.`,
    ],
    cities: [
      { name: "Kiowa", lat: 39.347, lng: -104.464 },
      { name: "Running Creek", lat: 39.39, lng: -104.6 },
      { name: "Ponderosa Park", lat: 39.4, lng: -104.63 },
    ],
  },
  {
    slug: "longmont",
    name: "Longmont",
    state: "CO",
    region: "Boulder County",
    isPrimary: false,
    lat: 40.1672,
    lng: -105.1019,
    description:
      "Longmont, CO painting and remodeling contractor. Residential and commercial interior and exterior painting, drywall, tile, and renovations.",
    highlights: [
      "Residential & commercial painting",
      "Drywall & popcorn ceiling removal",
      "Kitchen, bath & basement remodels",
      "15% new-client discount",
    ],
    content: [
      "Longmont blends century-old homes near Main Street with large newer subdivisions on the east side — plus a growing commercial and light-industrial base. We work all three, from historic trim restoration to warehouse and office interior repaints.",
      "Services in Longmont include interior and exterior painting, facade waterproofing, drywall and ceiling repair, tile installation, and complete kitchen, bathroom, or basement remodels.",
      `Call ${PHONE} for a free Longmont estimate.`,
    ],
    cities: [
      { name: "Old Town Longmont", lat: 40.167, lng: -105.101 },
      { name: "Prospect", lat: 40.15, lng: -105.106 },
      { name: "Niwot", lat: 40.104, lng: -105.171 },
      { name: "Firestone", lat: 40.112, lng: -104.937 },
    ],
  },
  {
    slug: "loveland",
    name: "Loveland",
    state: "CO",
    region: "Larimer County",
    isPrimary: false,
    lat: 40.3978,
    lng: -105.075,
    description:
      "Loveland, CO painting, drywall, tile, and remodeling services for homes and businesses across Larimer County.",
    highlights: [
      "Interior & exterior painting",
      "Facade waterproofing",
      "Drywall, tile & remodeling",
      "Free written estimates",
    ],
    content: [
      "Loveland is the northern edge of our service map and one of our fastest-growing areas. Homeowners here get the same crew and the same standards as our Douglas County clients — we simply schedule northern work in blocks to keep the travel efficient.",
      "We handle exterior and interior painting, waterproof coatings on stucco and masonry, drywall repair and texture matching, tile installation, and full remodels throughout Loveland.",
      `Call ${PHONE} for a free Loveland estimate.`,
    ],
    cities: [
      { name: "Downtown Loveland", lat: 40.397, lng: -105.075 },
      { name: "Berthoud", lat: 40.308, lng: -105.081 },
      { name: "Johnstown", lat: 40.336, lng: -104.912 },
      { name: "Windsor", lat: 40.477, lng: -104.901 },
    ],
  },
];

export const primaryLocation = locations.find((l) => l.isPrimary) ?? locations[0];
